import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useMemo } from 'react'

import {
  ResponsiveImage,
  Trash,
  Upload as UploadIcon,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useAuthentication } from '../../../../../../../../app/_providers/AuthenticationProvider.client'
import { useLocalization } from '../../../../../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../../../../../brunswick/containers/embroidery'
import { useFixturesContext } from '../../../../../../../../brunswick/containers/fixtures'
import { trackEvent } from '../../../../../../../../lib/analytics'
import { intlPrettyCurrency } from '../../../../../../../../lib/utils'
import { FormBody } from '../../shared-form-styles'
import { ErrorMessage } from '../../shared-form-styles'
import { PriceLabel, ThreadEmojiWrapper } from '../../shared-form-styles'
import * as Name from '../../TextEmbroideryForm/FormBody/Name'
import { LogoFormContainer } from '../LogoFormContainer'

import { LogoColorSelector } from './LogoColorSelector'
import * as LogoPreview from './LogoPreview'
import * as Note from './Note'
import * as SignIn from './SignIn'
import * as Styles from './styles'
import { TosCheckbox } from './TosCheckbox'
import * as UploadButton from './UploadButton'

const maxFilesSize = 3145728

export const EmbroideryLogoSetup: FC = () => {
  const { logoSetupForm } = LogoFormContainer.useContainer()
  const {
    embroidery: {
      modal: {
        forms: { dtcLogoEmbroideryForm: fixtures },
      },
    },
  } = useTranslation()
  const { logoLearnMorelink: logoLearnMoreLink, logoDigitizationWarning } = useFixturesContext()
  const { region, locale } = useLocalization()
  const { embroideryVariantsByKind } = EmbroideryService.useContainer()
  const { isLoggedIn } = useAuthentication()
  const isSmallViewport = useIsViewport(ViewportSizes['small'])

  const router = useRouter()
  const currentUrl = router.asPath

  const onFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetFiles = event.target.files

    if (targetFiles?.[0]) {
      if (targetFiles?.[0].size < maxFilesSize) {
        const file = targetFiles[0]
        logoSetupForm.setFieldValue('file', file)
      } else {
        logoSetupForm.setFieldError('file', fixtures.fileSizeError)
      }
    }
  }

  const setupPrice = intlPrettyCurrency({
    cents: embroideryVariantsByKind?.LOGO_SETUP?.price,
    region: region,
    locale: locale,
    truncateTrailingZeros: true,
  })

  const imgSrc = useMemo(() => {
    if (!logoSetupForm.values.file) return
    return URL.createObjectURL(logoSetupForm.values.file)
  }, [logoSetupForm.values.file])

  const imgFileName = useMemo(() => {
    if (!logoSetupForm.values.file) return
    return logoSetupForm.values.file instanceof File ? logoSetupForm.values.file.name : undefined
  }, [logoSetupForm.values.file])

  const isNoteExpanded = Boolean(logoSetupForm.values.notes)

  return (
    <FormBody onSubmit={logoSetupForm.handleSubmit}>
      <Styles.Label>
        <Styles.MicroCaps>{fixtures.title}</Styles.MicroCaps>
        <PriceLabel>{setupPrice}</PriceLabel>
      </Styles.Label>
      <Styles.WarningLabel>
        <ThreadEmojiWrapper aria-label='thread' role='img'>
          🧵
        </ThreadEmojiWrapper>
        <div>
          {logoDigitizationWarning + ` `}
          <Styles.LearnMoreLink
            target='_blank'
            href={logoLearnMoreLink ?? '#'}
            aria-label={fixtures.ariaLabelLearnMoreLink}
          >
            {fixtures.learnMore}
          </Styles.LearnMoreLink>
        </div>
      </Styles.WarningLabel>
      <LogoColorSelector form={logoSetupForm} />

      {imgSrc ? (
        <>
          <LogoPreview.Container>
            <LogoPreview.ImageContainer>
              <ResponsiveImage
                src={imgSrc}
                aspectRatios={{
                  sm: null,
                  md: null,
                }}
                loading='lazy'
                alt={fixtures.logoPreviewImageAlt}
                widths={{ unit: 'vw', sm: 100, md: 50 }}
                transparentBackground={true}
              />
              <LogoPreview.TrashButton
                onClick={() => logoSetupForm.setFieldValue('file', undefined)}
                aria-label={fixtures.ariaLabelRemoveLogo}
                type='button'
              >
                <Trash />
              </LogoPreview.TrashButton>
            </LogoPreview.ImageContainer>
          </LogoPreview.Container>
          <Styles.UploadedFileName>{imgFileName}</Styles.UploadedFileName>
          <ErrorMessage inset role='alert'>
            {logoSetupForm.errors['file']}
          </ErrorMessage>
          <Styles.StyledNameWrapper label='name-logo' title={fixtures.logoName} required>
            <Name.Input
              ariaLabelledby='name-logo'
              name='name'
              form={logoSetupForm}
              maxCharacterCount={50}
              placeholder={fixtures.logoNamePlaceholder}
            />
          </Styles.StyledNameWrapper>
          <Note.Accordion variant='light' icon='plus' ariaLabel={fixtures.noteLabel}>
            <Note.Section index={0} isActive={isNoteExpanded}>
              <Note.Header>{fixtures.noteLabel}</Note.Header>
              <Note.Body>
                <Note.TextArea
                  {...logoSetupForm.getFieldProps({
                    name: 'notes',
                    placeholder: 'Placeholder',
                    maxLength: 500,
                  })}
                />
              </Note.Body>
            </Note.Section>
          </Note.Accordion>
          <TosCheckbox form={logoSetupForm} />
          <Styles.Button
            type='submit'
            {...trackEvent({
              category: 'embroidery',
              action: 'next logo customization',
            })}
          >
            {fixtures.nextCta}
          </Styles.Button>
          {isSmallViewport ? (
            <ErrorMessage inset role='alert'>
              {logoSetupForm.errors['digitization_setting']}
            </ErrorMessage>
          ) : null}
        </>
      ) : (
        <>
          <UploadButton.Container>
            <UploadButton.Label>
              <UploadButton.Input
                type='file'
                accept='.jpg, .jpeg, .png, .eps'
                name='file'
                onChange={onFileSelect}
                {...trackEvent({
                  category: 'embroidery',
                  action: 'upload logo',
                })}
              />
              <UploadIcon />
              {fixtures.uploadCta}
            </UploadButton.Label>
          </UploadButton.Container>
          <ErrorMessage inset role='alert'>
            {logoSetupForm.errors['digitization_setting']}
          </ErrorMessage>
          <Styles.HeadlineMicro>{fixtures.uploadSpecs}</Styles.HeadlineMicro>
        </>
      )}
      {!isLoggedIn && (
        <>
          <SignIn.OrDivider>
            <SignIn.OrLine side='left' />
            <SignIn.OrText>{fixtures.existingLogoOrText}</SignIn.OrText>
            <SignIn.OrLine side='right' />
          </SignIn.OrDivider>
          <SignIn.ExistingLogoContainer>
            <SignIn.ExistingLogoTitle>{fixtures.existingLogoTitle}</SignIn.ExistingLogoTitle>
            <SignIn.ExistingLogoSubtitle>
              {fixtures.existingLogoSubtitle}
            </SignIn.ExistingLogoSubtitle>
            <SignIn.LoginTextLink
              as={Link}
              variant='underline-on-hover'
              href={`/account/login?redirect_url=${encodeURIComponent(currentUrl)}`}
            >
              {fixtures.loginCta}
            </SignIn.LoginTextLink>
          </SignIn.ExistingLogoContainer>
        </>
      )}
    </FormBody>
  )
}
